import * as AT from "./actionTypes";
import { get as Ajax_get, post as Ajax_post } from "../../app/Ajax";
import {
    API_URL_SERVICE_QUALIFICATION_ADDRESS_SEARCH,
    API_URL_VAID_FROM_ADDRESS,
    API_URL_SELECT_PACKAGE,
    API_URL_MOCK_ADDRESS,
    API_URL_SAVE_CONTACT_INFO,
    API_URL_SUBMIT_ORDER,
} from "../api";
import { getFormById, isBroadbandCustomer } from "./selectors";
import { VALIDATION_FIELD } from "../../rapidform/formValidation";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { RootState } from "../../../../models";
import type { HSSalesPackage } from "../../../../models/server/homeSales";
import TEXT from "../../checkout/text-consts";
import type { TeliaPlayResolvedMockAddress } from "../../../../shared/teliaPlayMockAddresses";

export const getVaidFromAddress = (addressId: number, dwellingUnitNumber: string) => ({
    type: AT.TELIA_PLAY_GET_VAID_FROM_ADDRESS,
    payload: Ajax_get<{ data?: { technologies?: { vaId?: number }[] } }>({
        url: `${API_URL_VAID_FROM_ADDRESS}?addressId=${encodeURIComponent(addressId.toString())}&dwellingUnitNumber=${encodeURIComponent(dwellingUnitNumber)}`,
    }),
});

export const searchServiceQualificationAddresses = (packageId: string) => ({
    type: AT.TELIA_PLAY_SEARCH_ADDRESSES,
    payload: Ajax_get<{ data: unknown[] }>({
        url: `${API_URL_SERVICE_QUALIFICATION_ADDRESS_SEARCH}?packageId=${encodeURIComponent(packageId)}`,
    }),
});

export const selectPackage = (packageId: string) => ({
    type: AT.TELIA_PLAY_SELECT_PACKAGE,
    payload: Ajax_post<{ data: { selectedPackage: HSSalesPackage } }>({
        url: API_URL_SELECT_PACKAGE,
        data: { packageId },
    }),
});

export const setCustomerSubmissionPending = (isPending: boolean) => ({
    type: isPending ? AT.TELIA_PLAY_CUSTOMER_SUBMISSION_STARTED : AT.TELIA_PLAY_CUSTOMER_SUBMISSION_FINISHED,
});

export const selectMockAddress = (mockId: number | null) => ({
    type: AT.TELIA_PLAY_SELECT_MOCK_ADDRESS,
    payload: Ajax_post<TeliaPlayResolvedMockAddress | null>({
        url: API_URL_MOCK_ADDRESS,
        data: { id: mockId },
    }),
});

export const clearMockAddress = () => selectMockAddress(null);

export const clearPackageUnavailable = () => ({ type: AT.TELIA_PLAY_CLEAR_PACKAGE_UNAVAILABLE });

type TeliaPlayThunkDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

const postContactInfo = async (dispatch: TeliaPlayThunkDispatch, getState: () => RootState): Promise<void> => {
    const { values } = getFormById(getState(), "personalDetailsTeliaPlayForm");

    const personalDetails = {
        email: values.details[VALIDATION_FIELD.PRODUCT_USER_EMAIL],
        phone: values.details[VALIDATION_FIELD.PRODUCT_USER_PHONE],
    };

    await dispatch({
        type: AT.TELIA_PLAY_SET_CONTACT_INFO,
        payload: Ajax_post(
            {
                url: API_URL_SAVE_CONTACT_INFO,
                data: { personalDetails },
            },
            dispatch,
        ),
    });
};

export const saveContactInfo =
    (
        addressId: number,
        selectedApartment: string,
        packageId: string,
    ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        await postContactInfo(dispatch, getState);
        await dispatch(getVaidFromAddress(addressId, selectedApartment));
        if (!isBroadbandCustomer(getState())) {
            await dispatch(selectPackage(packageId));
        }
    };

// AT only: the VAID of the selected mock address is already stored on the order, so no VAID lookup is needed
export const saveContactInfoWithMockVaid =
    (packageId: string): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        await postContactInfo(dispatch, getState);
        await dispatch(selectPackage(packageId));
    };

type SubmitOrderResult = { orderId: string; orderRef: string; success: boolean };

export const submitOrder =
    (): ThunkAction<Promise<SubmitOrderResult>, RootState, unknown, AnyAction> => async (dispatch) => {
        const { value } = (await dispatch({
            type: AT.TELIA_PLAY_SUBMIT_ORDER,
            payload: Ajax_post<{ orderId: string; orderRef: string }>(
                {
                    url: API_URL_SUBMIT_ORDER,
                },
                dispatch,
            ).then((res) => {
                return {
                    orderId: res.orderId,
                    orderRef: res.orderRef,
                    success: true,
                };
            }),
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.ORDER_SUBMIT,
                },
            },
        })) as unknown as { value: SubmitOrderResult };
        return value;
    };
